import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  BANK_ACCOUNTANT_ACCESS_STEPS,
  DEFAULT_BANK_ACCOUNTANT_ACCESS_STEPS,
} from "constants/bankAccountantAccessSteps";
import { SUPPORT_EMAIL } from "constants/base";
import { useState } from "react";

type GrantAccountantAccessModalProps = {
  open: boolean;
  bankName: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const Step = ({
  step,
  children,
  isLast,
}: {
  step: number;
  children: React.ReactNode;
  isLast?: boolean;
}) => {
  return (
    <div
      className={classNames("t-h-max t-pl-2.5 t-relative", {
        "t-border t-border-solid t-border-purple-20 t-border-t-0 t-border-r-0 t-border-b-0":
          !isLast,
      })}
    >
      <div className="t-absolute t-flex t-left-0 t-items-center t-justify-center -t-translate-x-1/2 t-bg-surface t-h-5 t-w-5 t-text-neutral-20">
        <span className="t-inline-flex t-w-4 t-h-4 t-rounded-full t-items-center t-justify-center t-text-caption t-text-white t-bg-purple-40">
          {step}
        </span>
      </div>
      <div className="t-ml-3 t-space-y-1 t-pb-4">
        <div className="t-text-text-60 t-mb-0 t-text-body-sm" role="listitem">
          {children}
        </div>
      </div>
    </div>
  );
};

export const GrantAccountantAccessModal = ({
  open,
  bankName,
  isLoading,
  onCancel,
  onConfirm,
}: GrantAccountantAccessModalProps) => {
  const [isAccessGrantedChecked, setIsAccessGrantedChecked] = useState(false);
  const footerButtonClassName = "sm:t-flex-1";

  const steps =
    BANK_ACCOUNTANT_ACCESS_STEPS[bankName] ??
    DEFAULT_BANK_ACCOUNTANT_ACCESS_STEPS;

  const handleOpenChange = (isModalOpen: boolean) => {
    if (!isModalOpen) {
      setIsAccessGrantedChecked(false);
      onCancel();
    }
  };

  const handleConfirm = () => {
    setIsAccessGrantedChecked(false);
    onConfirm();
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Grant account access in {bankName}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <p className="t-text-body-sm t-text-text-30 t-mb-4">
            Login to {bankName} and follow these simple steps to grant access.
          </p>
          <div className="t-space-y-1 sm:t-pl-3">
            {steps.map((step, index) => (
              <Step key={index} step={index + 1}>
                {step}
              </Step>
            ))}
            <Step step={steps.length + 1} isLast>
              Return to Inkle and confirm below that you have granted the access
              <div className="t-mt-3">
                <div className="t-w-full t-border-solid t-border t-border-blue-30 t-rounded-lg t-px-2 t-py-3 t-bg-blue-0 t-break-words">
                  <Checkbox
                    alignTop
                    label={`I've granted Accountant (read-only) access to ${SUPPORT_EMAIL}`}
                    checked={isAccessGrantedChecked}
                    onChange={(e) =>
                      setIsAccessGrantedChecked(e.target.checked)
                    }
                  />
                </div>
              </div>
            </Step>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button
            customType="secondary"
            onClick={onCancel}
            className={footerButtonClassName}
          >
            Cancel
          </Button>
          <Button
            customType="primary"
            onClick={handleConfirm}
            className={footerButtonClassName}
            disabled={!isAccessGrantedChecked || isLoading}
            isLoading={isLoading}
          >
            Confirm
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
